import React, { memo, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import IpdDateField from "../../../../utils/IpdDateField";
import FormField from "../FormField";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import { usePatientSearch } from "../../hooks/usePatientSearch";
import { SEARCH_BY_OPTIONS } from "../../constants/registrationConstants";
import { fetchPatientAdmissionData } from "../../api/registrationService";
import { mapAdmissionDataToPatient } from "../../utils/mapAdmissionDataToPatient";
import { normalizeBase64Image } from "../../utils/patientSearch";

import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";

function SearchPatientDialog({ open, onClose, onDone, onViewPatient }) {
  const [searchType, setSearchType] = useState("abhaId");
  const [searchQuery, setSearchQuery] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");

  const { results, loading, runSearch, reset } = usePatientSearch();
  const [loadingPatientId, setLoadingPatientId] = useState(null);

  const isDemographics = searchType === "demographics";

  useEffect(() => {
    if (!open) {
      setSearchType("abhaId");
      setSearchQuery("");
      setName("");
      setGender("");
      setDob("");
      reset();
      setLoadingPatientId(null);
    }
  }, [open, reset]);

  const handleSearch = async (event) => {
    event?.preventDefault();

    if (!isDemographics && !String(searchQuery || "").trim()) {
      toast.info("Enter a search query");
      return;
    }

    const { patients, error } = await runSearch({
      searchType,
      searchQuery: searchQuery.trim(),
      name: name.trim(),
      gender,
      dob,
    });

    if (error) {
      toast.error(error);
      return;
    }

    if (patients.length > 0) {
      toast.success("Patient Found Successfully");
    }
  };

  const handleViewPatient = async (patient) => {
    const patientId = patient?.patient_id;
    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    setLoadingPatientId(patientId);
    try {
      const result = await fetchPatientAdmissionData(patientId, { silent: true });
      const mapped = mapAdmissionDataToPatient(result.data, {
        patient_id: result.patient_id,
        uhid: result.uhid,
        admission_id: result.admission_id,
      });

      onViewPatient({
        patient: mapped,
        patient_id: result.patient_id,
        uhid: result.uhid,
        admission_id: result.admission_id,
      });
    } catch (err) {
      toast.error(err.message || "Failed to load patient admission data");
    } finally {
      setLoadingPatientId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="search-patient-title">
      <DialogTitle id="search-patient-title" sx={{ color: C.textPrimary, fontWeight: 700 }}>
        Search / Edit Patient
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSearch}>
          <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormField label="Search by">
                <IpdTextField
                  select
                  fullWidth
                  size="small"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  {SEARCH_BY_OPTIONS.map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </IpdTextField>
              </FormField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField label="Enter Search Query">
                <IpdTextField
                  fullWidth
                  size="small"
                  placeholder="Query"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isDemographics}
                />
              </FormField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField label="Name">
                <IpdTextField
                  fullWidth
                  size="small"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isDemographics}
                />
              </FormField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField label="Gender">
                <IpdTextField
                  select
                  fullWidth
                  size="small"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  disabled={!isDemographics}
                >
                  <MenuItem value="">Gender</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                  <MenuItem value="O">Other</MenuItem>
                </IpdTextField>
              </FormField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField label="Date of Birth">
                <IpdDateField
                  value={dob}
                  onChange={setDob}
                  disabled={!isDemographics}
                  maxDate={new Date()}
                />
              </FormField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ mt: 1 }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={onDone}
                sx={{ mt: 1 }}
              >
                Done
              </Button>
            </Grid>
          </Grid>
        </Box>

        {results.length > 0 && (
          <Box
            sx={{
              height: 500,
              overflow: "auto",
              mt: 2,
              p: { xs: 0.5, lg: 2 },
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              bgcolor: C.pageBg,
            }}
          >
            <Grid container spacing={1}>
              {results.map((patient, index) => (
                <Grid item xs={12} md={6} key={patient.patient_id || index}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      boxShadow: C.shadow.card,
                      bgcolor: C.cardBg,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <CardContent>
                      <Grid
                        container
                        alignItems="center"
                        spacing={2}
                        sx={{ minHeight: { md: 200, lg: 200 } }}
                      >
                        <Grid item xs={12} sm={3} md={12} lg={3} display="flex" justifyContent="center">
                          <Avatar
                            alt={patient.name}
                            src={normalizeBase64Image(patient.photo)}
                            sx={{ width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 } }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={8} md={9} lg={8}>
                          <Typography variant="h6" color={C.textPrimary}>{patient.name}</Typography>
                          <Typography variant="body2" color={C.textSecondary}>ABHA Number: {patient.abha_no || "-"}</Typography>
                          <Typography variant="body2" color={C.textSecondary}>Patient ID: {patient.patient_id}</Typography>
                          <Typography variant="body2" color={C.textSecondary}>ABHA Address: {patient.abha_id || "-"}</Typography>
                          <Typography variant="body2" color={C.textSecondary}>Gender: {patient.gender}</Typography>
                          <Typography variant="body2" color={C.textSecondary}>DOB: {patient.dob}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={1} md={3} lg={1} display="flex" justifyContent="center">
                          <IconButton
                            onClick={() => handleViewPatient(patient)}
                            aria-label="view details"
                            disabled={Boolean(loadingPatientId)}
                            sx={{ color: C.primary }}
                          >
                            {loadingPatientId === patient.patient_id
                              ? <CircularProgress size={22} color="inherit" />
                              : <VisibilityIcon />}
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(SearchPatientDialog);
